import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  CheckCircle, 
  Cancel,
  Send,
  Save
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { buildApiUrl } from '../config/api';

const AdminEmail = () => {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // SMTP Configuration state
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_security: 'TLS',
    smtp_username: '',
    smtp_password: '',
    sender_email: '',
    sender_name: 'Text Similarity Research System',
    enabled: true
  });
  const [smtpExists, setSmtpExists] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  
  const loadSmtpConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('admin/email/smtp'), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          // Valid SMTP config exists
          setSmtpConfig({
            ...data,
            smtp_password: '' // Don't load encrypted password
          });
          setSmtpExists(true);
          setTestStatus(data.test_verified ? 'verified' : null);
        } else {
          // Response is null or empty, no config exists
          setSmtpExists(false);
        }
      } else if (response.status === 404) {
        // No SMTP config exists yet
        setSmtpExists(false);
      } else {
        throw new Error('Failed to load SMTP configuration');
      }
    } catch (error) {
      console.error('Failed to load SMTP configuration:', error);
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  // Load SMTP configuration
  useEffect(() => {
    loadSmtpConfig();
  }, [loadSmtpConfig]);

  const handleSmtpChange = (field, value) => {
    setSmtpConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error details when user changes config
    if (errorDetails) {
      setErrorDetails(null);
    }
  };

  // Validate SMTP configuration
  const validateSmtpConfig = () => {
    const warnings = [];
    
    // AWS SES validation
    if (smtpConfig.smtp_host.includes('amazonaws.com')) {
      if (!smtpConfig.smtp_host.startsWith('email-smtp.')) {
        warnings.push('Para AWS SES, o host deve começar com "email-smtp.região.amazonaws.com"');
      }
      if (smtpConfig.smtp_port !== 587 && smtpConfig.smtp_port !== 465) {
        warnings.push('Para AWS SES, use porta 587 (TLS) ou 465 (SSL)');
      }
      if (smtpConfig.smtp_security !== 'TLS' && smtpConfig.smtp_security !== 'SSL') {
        warnings.push('Para AWS SES, use TLS (porta 587) ou SSL (porta 465)');
      }
    }
    
    // Gmail validation
    if (smtpConfig.smtp_host.includes('gmail.com')) {
      if (smtpConfig.smtp_port !== 587) {
        warnings.push('Para Gmail, use porta 587 com TLS');
      }
      if (smtpConfig.smtp_security !== 'TLS') {
        warnings.push('Para Gmail, use TLS como segurança');
      }
    }
    
    // General validations
    if (smtpConfig.smtp_port < 1 || smtpConfig.smtp_port > 65535) {
      warnings.push('Porta SMTP deve estar entre 1 e 65535');
    }
    
    return warnings;
  };

  const saveSMTPConfig = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!smtpConfig.smtp_host || !smtpConfig.smtp_username || 
          !smtpConfig.sender_email || (!smtpExists && !smtpConfig.smtp_password)) {
        setMessage({ text: 'Por favor, preencha todos os campos obrigatórios', type: 'error' });
        return;
      }
      
      const method = smtpExists ? 'PATCH' : 'POST';
      console.log(`Saving SMTP config using ${method}, smtpExists: ${smtpExists}`);
      
      // Only send password if it was changed
      const configData = { ...smtpConfig };
      if (smtpExists && !configData.smtp_password) {
        delete configData.smtp_password;
      }
      
      const response = await fetch(buildApiUrl('admin/email/smtp'), {
        method,
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save SMTP configuration');
      }
      
      setMessage({ text: 'Configuração SMTP salva com sucesso', type: 'success' });
      setSmtpExists(true);
      setTestStatus(null); // Reset test status when config changes
      
      // Clear password field after save
      setSmtpConfig(prev => ({ ...prev, smtp_password: '' }));
    } catch (error) {
      setMessage({ 
        text: error.message || 'Falha ao salvar configuração SMTP', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ text: 'Por favor, insira um endereço de email para teste', type: 'error' });
      return;
    }
    
    try {
      setLoading(true);
      setTestStatus('testing');
      
      const response = await fetch(buildApiUrl('admin/email/test'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: testEmail
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        setErrorDetails(errorData.details || null);
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setTestStatus('verified');
      setErrorDetails(null);
      setMessage({ 
        text: `Email de teste enviado com sucesso! Verifique sua caixa de entrada em ${testEmail}`, 
        type: 'success' 
      });
      
      // Reload config to get updated test status
      await loadSmtpConfig();
    } catch (error) {
      setTestStatus('failed');
      let errorMessage = error.message || 'Falha ao enviar email de teste';
      
      // Parse detailed error information
      const errorText = errorMessage.toLowerCase();
      
      if (errorText.includes('authentication') || errorText.includes('auth')) {
        errorMessage = 'Erro de autenticação SMTP: Verifique as credenciais (usuário e senha). Para AWS SES, certifique-se de usar as credenciais SMTP específicas, não as credenciais da conta AWS.';
      } else if (errorText.includes('connection') || errorText.includes('connect')) {
        errorMessage = 'Erro de conexão SMTP: Verifique o host e a porta. Para AWS SES, use email-smtp.{região}.amazonaws.com (ex: email-smtp.us-east-1.amazonaws.com) na porta 587.';
      } else if (errorText.includes('email address is not verified') || errorText.includes('not verified')) {
        errorMessage = 'Erro: Email não verificado. Para AWS SES no modo sandbox, você precisa verificar tanto o email de origem quanto o de destino no console da AWS.';
      } else if (errorText.includes('recipients refused') || errorText.includes('refused')) {
        errorMessage = 'Destinatário rejeitado: Para AWS SES no modo sandbox, apenas emails verificados podem receber mensagens. Verifique o email de destino no console AWS ou solicite saída do modo sandbox.';
      } else if (errorText.includes('ssl') || errorText.includes('tls')) {
        errorMessage = 'Erro de segurança SSL/TLS: Para AWS SES, use TLS na porta 587 ou SSL na porta 465.';
      } else if (errorText.includes('timeout')) {
        errorMessage = 'Timeout de conexão: Verifique se as portas 587 (TLS) ou 465 (SSL) estão abertas no firewall.';
      } else if (errorText.includes('authentication failed')) {
        errorMessage = 'Erro: Falha na autenticação. Verifique o usuário e senha SMTP. Para Gmail, use uma senha de app.';
      } else if (errorMessage.includes('Connection refused')) {
        errorMessage = 'Erro: Conexão recusada. Verifique o servidor e porta SMTP.';
      }
      
      setMessage({ 
        text: errorMessage, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user is loaded and admin
  if (!auth.isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (auth.user === null) {
    // Still loading user data
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Carregando...</Typography>
      </Container>
    );
  }
  
  if (auth.user && auth.user.role !== 'ADMIN') {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Configuração de Email
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ text: '', type: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Error Details */}
        {errorDetails && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffebee' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Detalhes do Erro
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Servidor SMTP:</strong> {errorDetails.smtp_host || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Porta:</strong> {errorDetails.smtp_port || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Segurança:</strong> {errorDetails.smtp_security || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Email Remetente:</strong> {errorDetails.sender_email || 'N/A'}
                </Typography>
              </Grid>
              {errorDetails.error_type && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Tipo de Erro:</strong> {errorDetails.error_type}
                  </Typography>
                </Grid>
              )}
            </Grid>
            
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                💡 Dicas para AWS SES:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li><strong>Host:</strong> email-smtp.{'{região}'}.amazonaws.com (ex: email-smtp.us-east-1.amazonaws.com)</li>
                  <li><strong>Porta:</strong> 587 (TLS) ou 465 (SSL)</li>
                  <li><strong>Credenciais:</strong> Use as credenciais SMTP específicas do SES, não as da conta AWS</li>
                  <li><strong>Modo Sandbox:</strong> Verifique emails de origem e destino no console AWS</li>
                  <li><strong>Domínio:</strong> Configure e verifique seu domínio no SES</li>
                </ul>
              </Typography>
            </Box>
          </Paper>
        )}

        {/* SMTP Configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Configuração do Servidor SMTP</Typography>
            {testStatus && (
              <Chip 
                icon={testStatus === 'verified' ? <CheckCircle /> : <Cancel />}
                label={testStatus === 'verified' ? 'Verificado' : 'Não Verificado'}
                color={testStatus === 'verified' ? 'success' : 'error'}
                size="small"
              />
            )}
          </Box>

          {/* Configuration Warnings */}
          {(() => {
            const warnings = validateSmtpConfig();
            return warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Avisos de Configuração:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            );
          })()}
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Servidor SMTP"
                value={smtpConfig.smtp_host}
                onChange={(e) => handleSmtpChange('smtp_host', e.target.value)}
                required
                helperText="ex: smtp.gmail.com"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Porta SMTP"
                type="number"
                value={smtpConfig.smtp_port}
                onChange={(e) => handleSmtpChange('smtp_port', parseInt(e.target.value))}
                required
                helperText="Geralmente 587 para TLS"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Segurança</InputLabel>
                <Select
                  value={smtpConfig.smtp_security}
                  onChange={(e) => handleSmtpChange('smtp_security', e.target.value)}
                  label="Segurança"
                >
                  <MenuItem value="TLS">TLS</MenuItem>
                  <MenuItem value="SSL">SSL</MenuItem>
                  <MenuItem value="NONE">Nenhuma</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Usuário SMTP"
                value={smtpConfig.smtp_username}
                onChange={(e) => handleSmtpChange('smtp_username', e.target.value)}
                required
                helperText="Geralmente seu endereço de email"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Senha SMTP"
                type={showPassword ? 'text' : 'password'}
                value={smtpConfig.smtp_password}
                onChange={(e) => handleSmtpChange('smtp_password', e.target.value)}
                required={!smtpExists}
                placeholder={smtpExists ? "Deixe em branco para manter a senha atual" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email do Remetente"
                type="email"
                value={smtpConfig.sender_email}
                onChange={(e) => handleSmtpChange('sender_email', e.target.value)}
                required
                helperText="Endereço de email que aparecerá como remetente"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Remetente"
                value={smtpConfig.sender_name}
                onChange={(e) => handleSmtpChange('sender_name', e.target.value)}
                helperText="Nome de exibição do remetente"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpConfig.enabled}
                    onChange={(e) => handleSmtpChange('enabled', e.target.checked)}
                  />
                }
                label="Habilitar notificações por email"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  onClick={saveSMTPConfig}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  Salvar Configuração
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Test Email */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Testar Configuração de Email
          </Typography>

          {/* Last test result */}
          {smtpConfig.last_test_date && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: smtpConfig.test_verified ? '#e8f5e8' : '#ffebee', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Último Teste: {new Date(smtpConfig.last_test_date).toLocaleString('pt-BR')}
              </Typography>
              <Typography variant="body2" color={smtpConfig.test_verified ? 'success.main' : 'error.main'}>
                <strong>Resultado:</strong> {smtpConfig.last_test_result || 'Resultado não disponível'}
              </Typography>
            </Box>
          )}
          
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              label="Endereço de Email para Teste"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="seu-email@exemplo.com"
              sx={{ flex: 1 }}
              disabled={!smtpExists}
              helperText={!smtpExists ? "Salve a configuração SMTP primeiro" : ""}
            />
            <Button
              variant="contained"
              onClick={sendTestEmail}
              disabled={loading || !smtpExists || testStatus === 'testing'}
              startIcon={
                testStatus === 'testing' ? <CircularProgress size={20} /> : <Send />
              }
            >
              {testStatus === 'testing' ? 'Enviando...' : 'Enviar Email de Teste'}
            </Button>
          </Box>
        </Paper>
      </Container>
  );
};

export default AdminEmail;